// FilesSection.jsx
import React, { useState } from 'react';
import { Download, ExternalLink, File, Maximize2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ProjectService } from '@/services/ProjectService';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {FilePreview} from '@/components/FileUploadCard';

const formatFileName = (fileName) => {
  // Remove file extension
  const nameWithoutExt = fileName.split('.').slice(0, -1).join('.');
  // Replace underscores with spaces and capitalize first letter of each word
  return nameWithoutExt
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const FileLinkSummary = ({ fileItems, linkItems }) => {
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  if (!fileItems?.length && !linkItems?.length) {
    return <p className="text-gray-500">No files or links added</p>
  }

  const handleDownload = async (file) => {
    try {
      const downloadedFile = await ProjectService.downloadFileFromS3(file.s3Key)
      const url = window.URL.createObjectURL(downloadedFile)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading file:', error)
    }
  }

  const openPreview = async (fileItem) => {
    setIsPreviewLoading(true);
    setSelectedPreview({
      type: fileItem.type,
      name: fileItem.name,
      s3Key: fileItem.s3Key,
      thumbnail: fileItem.thumbnail
    });
    setIsPreviewOpen(true);

    try {
      const file = await ProjectService.downloadFileFromS3(fileItem.s3Key);
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setSelectedPreview(prev => ({
          ...prev,
          url: previewUrl
        }));
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setIsPreviewLoading(false);
    }
  };
  return (
    <div className="space-y-6">
      {fileItems?.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Uploaded Files</h3>
          <div className="space-y-4">
            {fileItems.map((file) => (
              <div key={file.id} className="p-4 border rounded-lg">
                <div className="flex gap-4">
                  <div 
                    className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 group relative cursor-pointer"
                    onClick={() => openPreview(file)}
                  >
                    {file.thumbnail ? (
                      <>
                        <img
                          src={file.thumbnail}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <Maximize2 className="text-white opacity-0 group-hover:opacity-100 w-6 h-6" />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <File className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between items-start">
                      <span className="font-medium">{formatFileName(file.name)}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(file)}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                    {file.notes && (
                      <p className="text-gray-600 mb-2">{file.notes}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{file.type}</span>
                      <span>•</span>
                      <span>
                        {file.size > 1024 * 1024 
                          ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                          : `${(file.size / 1024).toFixed(2)} KB`}
                      </span>
                      <span>•</span>
                      <span>
                        {new Date(file.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {linkItems?.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Saved Links</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {linkItems.map((link) => (
              <Card key={link.id} className="p-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1">
                    <a 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline text-sm break-all"
                    >
                      {link.url}
                    </a>
                    {link.notes && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{link.notes}</p>
                    )}
                  </div>
                  <Button variant="outline" size="icon" asChild>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl w-full p-6">
          <DialogHeader>
            <DialogTitle className="sr-only">File Preview</DialogTitle>
          </DialogHeader>
          <div className="p-6">
            {isPreviewLoading ? (
              <div className="flex flex-col items-center justify-center h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <span className="mt-2">Loading preview...</span>
              </div>
            ) : (
              <FilePreview file={selectedPreview} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
};

export const FilesSection = ({ projectData, onEdit, isEditable }) => (
  <Card className="mb-4">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-xl font-bold">Files & Links</CardTitle>
      {isEditable && (
        <Button variant="outline" size="sm" onClick={() => onEdit("upload")}>
          Edit
        </Button>
      )}
    </CardHeader>
    <CardContent>
      <FileLinkSummary 
        fileItems={projectData?.fileItems ? typeof projectData.fileItems === 'string' ? JSON.parse(projectData.fileItems) : projectData.fileItems : []}
        linkItems={projectData?.linkItems ? typeof projectData.linkItems === 'string' ? JSON.parse(projectData.linkItems) : projectData.linkItems : []}
      />
    </CardContent>
  </Card>
);